import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Tooltip, Chip, CircularProgress,
  Autocomplete, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, Settings } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { expenseCategoryService } from '../services/custodySettlements.service';
import { chartOfAccountService } from '../services/chartOfAccount.service';
import {
  CustodyExpenseCategory,
  CreateCustodyExpenseCategory,
  UpdateCustodyExpenseCategory
} from '../types/custodySettlements.types';

const translations = {
  en: {
    title: 'Custody Expense Categories',
    subtitle: 'Configure expense categories linked to chart of accounts',
    add: 'Add Category',
    edit: 'Edit Category',
    nameEn: 'Category Name (EN)',
    nameAr: 'Category Name (AR)',
    account: 'Account',
    active: 'Active',
    sortOrder: 'Sort Order',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this category?',
    saved: 'Category saved successfully',
    updated: 'Category updated successfully',
    deleted: 'Category deleted successfully',
    noData: 'No expense categories found',
    requiresCostCenter: 'Requires Cost Center',
    actions: 'Actions',
    loading: 'Loading...',
    selectAccount: 'Select Account',
    accountMustBeFinal: 'Account must be a final (leaf) account',
    branchType: 'Branch Type',
    branchTypeAdmin: 'Admin (No Branch)',
    branchTypeOperational: 'Operational',
    branchTypeBakery: 'Bakery',
    branchTypeAny: 'Any Branch',
  },
  ar: {
    title: 'فئات مصاريف العُهد',
    subtitle: 'إعداد فئات المصاريف المرتبطة بشجرة الحسابات',
    add: 'إضافة فئة',
    edit: 'تعديل فئة',
    nameEn: 'اسم الفئة (إنجليزي)',
    nameAr: 'اسم الفئة (عربي)',
    account: 'الحساب',
    active: 'نشط',
    sortOrder: 'الترتيب',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    confirmDelete: 'هل أنت متأكد من حذف هذه الفئة؟',
    saved: 'تم حفظ الفئة بنجاح',
    updated: 'تم تحديث الفئة بنجاح',
    deleted: 'تم حذف الفئة بنجاح',
    noData: 'لا توجد فئات مصاريف',
    requiresCostCenter: 'يتطلب مركز تكلفة',
    actions: 'الإجراءات',
    loading: 'جاري التحميل...',
    selectAccount: 'اختر الحساب',
    accountMustBeFinal: 'يجب أن يكون الحساب نهائي (ليس رئيسي)',
    branchType: 'نوع الفرع',
    branchTypeAdmin: 'إدارية (بدون فرع)',
    branchTypeOperational: 'تشغيل',
    branchTypeBakery: 'مخبز',
    branchTypeAny: 'أي فرع',
  },
};

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  isFinalAccount: boolean;
  requireCostCenter: boolean;
}

const CustodyExpenseCategoriesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [categories, setCategories] = useState<CustodyExpenseCategory[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CustodyExpenseCategory | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [formData, setFormData] = useState<CreateCustodyExpenseCategory>({
    categoryName: '',
    categoryNameAr: '',
    accountId: 0,
    branchType: 'Admin',
    isActive: true,
    sortOrder: 0,
  });

  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, accountsRes] = await Promise.all([
        expenseCategoryService.getAll(),
        chartOfAccountService.getAll(),
      ]);
      setCategories(categoriesRes.data);
      setAccounts((accountsRes as unknown as Account[]).filter(a => a.isFinalAccount));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: CustodyExpenseCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        categoryName: category.categoryName,
        categoryNameAr: category.categoryNameAr,
        accountId: category.accountId,
        branchType: (category as any).branchType || 'Admin',
        isActive: category.isActive,
        sortOrder: category.sortOrder,
      });
      const acc = accounts.find(a => a.accountId === category.accountId);
      setSelectedAccount(acc || null);
    } else {
      setEditingCategory(null);
      setFormData({ categoryName: '', categoryNameAr: '', accountId: 0, branchType: 'Admin', isActive: true, sortOrder: 0 });
      setSelectedAccount(null);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.categoryName || !formData.categoryNameAr || !formData.accountId) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    try {
      setSaving(true);
      if (editingCategory) {
        const updateData: UpdateCustodyExpenseCategory = {
          ...formData,
          categoryId: editingCategory.categoryId,
        };
        await expenseCategoryService.update(editingCategory.categoryId, updateData);
        toast.success(t.updated);
      } else {
        await expenseCategoryService.create(formData);
        toast.success(t.saved);
      }
      setDialogOpen(false);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error saving category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await expenseCategoryService.delete(deletingId);
      toast.success(t.deleted);
      setDeleteConfirmOpen(false);
      setDeletingId(null);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error deleting category');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <Settings color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          {t.add}
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? t.nameAr : t.nameEn}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                {isRTL ? t.nameEn : t.nameAr}
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.account}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branchType}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.requiresCostCenter}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.active}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.sortOrder}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary" py={3}>{t.noData}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat, idx) => (
                <TableRow key={cat.categoryId} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{isRTL ? cat.categoryNameAr : cat.categoryName}</TableCell>
                  <TableCell>{isRTL ? cat.categoryName : cat.categoryNameAr}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cat.accountCode} - {isRTL ? cat.accountNameAr : cat.accountName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(cat as any).branchType === 'Admin' && (
                      <Chip label={t.branchTypeAdmin} size="small" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 600 }} />
                    )}
                    {(cat as any).branchType === 'Operational' && (
                      <Chip label={t.branchTypeOperational} size="small" sx={{ bgcolor: '#E65100', color: 'white', fontWeight: 600 }} />
                    )}
                    {(cat as any).branchType === 'Bakery' && (
                      <Chip label={t.branchTypeBakery} size="small" sx={{ bgcolor: '#4E342E', color: 'white', fontWeight: 600 }} />
                    )}
                    {!(cat as any).branchType && (
                      <Chip label={t.branchTypeAny} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {cat.requireCostCenter ? (
                      <Chip label={isRTL ? 'نعم' : 'Yes'} color="info" size="small" />
                    ) : (
                      <Chip label={isRTL ? 'لا' : 'No'} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cat.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'معطل' : 'Inactive')}
                      color={cat.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{cat.sortOrder}</TableCell>
                  <TableCell>
                    <Tooltip title={t.edit}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenDialog(cat)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t.delete}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => { setDeletingId(cat.categoryId); setDeleteConfirmOpen(true); }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? t.edit : t.add}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t.nameEn}
              value={formData.categoryName}
              onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t.nameAr}
              value={formData.categoryNameAr}
              onChange={(e) => setFormData({ ...formData, categoryNameAr: e.target.value })}
              fullWidth
              required
              inputProps={{ dir: 'rtl' }}
            />
            <FormControl fullWidth>
              <InputLabel>{t.branchType}</InputLabel>
              <Select
                value={formData.branchType || ''}
                label={t.branchType}
                onChange={(e) => setFormData({ ...formData, branchType: e.target.value || null })}
              >
                <MenuItem value="Admin">
                  <Chip label={t.branchTypeAdmin} size="small" sx={{ bgcolor: '#1565C0', color: 'white' }} />
                </MenuItem>
                <MenuItem value="Operational">
                  <Chip label={t.branchTypeOperational} size="small" sx={{ bgcolor: '#E65100', color: 'white' }} />
                </MenuItem>
                <MenuItem value="Bakery">
                  <Chip label={t.branchTypeBakery} size="small" sx={{ bgcolor: '#4E342E', color: 'white' }} />
                </MenuItem>
              </Select>
            </FormControl>
            <Autocomplete
              options={accounts}
              value={selectedAccount}
              onChange={(_, newValue) => {
                setSelectedAccount(newValue);
                setFormData({ ...formData, accountId: newValue?.accountId || 0 });
              }}
              getOptionLabel={(option) =>
                `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t.account}
                  required
                  helperText={t.accountMustBeFinal}
                />
              )}
              fullWidth
            />
            <TextField
              label={t.sortOrder}
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t.active}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t.delete}</DialogTitle>
        <DialogContent>
          <Typography>{t.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t.delete}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustodyExpenseCategoriesPage;